"use client";

import Link from "next/link";
import { useParams, useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import Icon from "../../../components/Icon";
import ProductImage from "../../../components/ProductImage";
import { ApiError, api, hasSession } from "../../../lib/api";

interface Product {
  id: string; sku: string; name: string; description?: string | null;
  stock: number; lowStock: boolean; hasImage: boolean; imageKey?: string | null;
  priceGs: number | null; priceListName: string | null;
  priceListKind: string | null; publicPriceGs: number | null;
}

/** Lo que devuelve POST /cupones/validar: mira el cupón sin consumirlo. */
interface Validacion {
  ok: boolean;
  /** Ya viene redactado en español por el servidor: se muestra tal cual. */
  motivo: string | null;
  descuentoGs: number;
  cupon: { code: string; kind: string; value: number; expiresAt: string | null } | null;
}

/** El código aplicado junto al monto sobre el que se validó: si cambia la
 *  cantidad, ese monto deja de coincidir y hay que volver a preguntar. */
interface CuponAplicado { code: string; descuentoGs: number; montoGs: number }

const gs = (n: number) => `₲ ${n.toLocaleString("es-PY")}`;

/** El ?next= devuelve al usuario al repuesto que estaba mirando. Se valida que
 *  sea una ruta nuestra: con "//otro-sitio" el login sería un trampolín afuera. */
function volverA(ruta: string) {
  const segura = ruta.startsWith("/") && !ruta.startsWith("//") ? ruta : "/tienda";
  return `/ingresar?next=${encodeURIComponent(segura)}`;
}

// Un descuento de 0% (o negativo por ruido de datos) no es una oferta
const descuentoDe = (p: Product) =>
  p.publicPriceGs != null && p.priceGs != null && p.publicPriceGs > p.priceGs
    ? Math.round((1 - p.priceGs / p.publicPriceGs) * 100)
    : 0;

export default function DetalleProducto() {
  const { id } = useParams<{ id: string }>();
  const router = useRouter();
  const [p, setP] = useState<Product | null>(null);
  const [qty, setQty] = useState(1);
  const [error, setError] = useState("");
  const [errorCarga, setErrorCarga] = useState("");
  const [busy, setBusy] = useState(false);
  const [sesion, setSesion] = useState(false);
  const [delivery, setDelivery] = useState<"PICKUP" | "SHIPPING">("PICKUP");
  const [address, setAddress] = useState("");
  // ── Cupón ──
  // Plegado por defecto: el que no tiene ninguno no se entera de que existe.
  const [cuponAbierto, setCuponAbierto] = useState(false);
  const [cuponTexto, setCuponTexto] = useState("");
  const [cupon, setCupon] = useState<CuponAplicado | null>(null);
  const [cuponMotivo, setCuponMotivo] = useState("");
  const [validando, setValidando] = useState(false);

  // Subtotal de la mercadería, antes del cupón: es exactamente el número que el
  // API controla contra expectedTotalGs.
  const subtotal = (p?.priceGs ?? 0) * qty;
  // Solo vale el descuento validado contra ESTE subtotal: mostrar el de la
  // cantidad anterior sería prometer una plata que el servidor no va a hacer.
  const descuentoGs = cupon && cupon.montoGs === subtotal ? Math.min(cupon.descuentoGs, subtotal) : 0;
  const totalFinal = subtotal - descuentoGs;

  // Pregunta cuánto descuenta el cupón sobre un monto, sin consumirlo. Nunca
  // borra lo que el usuario escribió: si falla, el código queda en el campo.
  const aplicarCupon = useCallback(async (codigo: string, monto: number) => {
    const limpio = codigo.trim();
    if (!limpio) { setCuponMotivo("Escribí el código del cupón."); return; }
    if (monto <= 0) { setCuponMotivo("Elegí una cantidad para usar el cupón."); return; }
    if (!hasSession()) {
      setCuponMotivo("Ingresá a tu cuenta para usar un cupón.");
      return;
    }
    setValidando(true); setCuponMotivo("");
    try {
      const r = await api<Validacion>("/cupones/validar", {
        method: "POST",
        body: JSON.stringify({ codigo: limpio, montoGs: monto }),
      });
      if (r.ok && r.descuentoGs > 0) {
        // El code que devuelve el servidor ya viene normalizado a BUSCO-XXXXXX
        setCupon({ code: r.cupon?.code ?? limpio, descuentoGs: r.descuentoGs, montoGs: monto });
        setCuponTexto(r.cupon?.code ?? limpio);
        setCuponMotivo("");
      } else {
        setCupon(null);
        setCuponMotivo(r.motivo ?? "Ese cupón no se puede usar en esta compra.");
      }
    } catch (e) {
      setCupon(null);
      setCuponMotivo(e instanceof ApiError ? e.message : "No pudimos validar el cupón. Probá de nuevo.");
    } finally {
      setValidando(false);
    }
  }, []);

  // Cambiar la cantidad cambia el descuento de un porcentual (y puede dejar por
  // debajo de la compra mínima): se vuelve a preguntar en vez de mostrar un
  // número que ya no es.
  useEffect(() => {
    if (!cupon || subtotal <= 0 || cupon.montoGs === subtotal) return;
    let vivo = true;
    (async () => {
      try {
        const r = await api<Validacion>("/cupones/validar", {
          method: "POST",
          body: JSON.stringify({ codigo: cupon.code, montoGs: subtotal }),
        });
        if (!vivo) return;
        if (r.ok && r.descuentoGs > 0) {
          setCupon({ code: cupon.code, descuentoGs: r.descuentoGs, montoGs: subtotal });
        } else {
          setCupon(null);
          setCuponAbierto(true);
          setCuponMotivo(r.motivo ?? "Ese cupón ya no se puede usar con esta cantidad.");
        }
      } catch {
        // Sin red no se puede saber: se deja puesto y decide el servidor al
        // confirmar. El desglose deja de mostrarlo descontado mientras tanto.
      }
    })();
    return () => { vivo = false; };
  }, [cupon, subtotal]);

  const cargar = useCallback(() => {
    setErrorCarga("");
    setP(null);
    api<Product>(`/store/products/${id}`)
      .then(setP)
      .catch((e) =>
        setErrorCarga(
          e instanceof ApiError
            ? e.message
            : "No pudimos conectarnos. Revisá tu conexión y probá de nuevo.",
        ),
      );
  }, [id]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { setSesion(hasSession()); }, []);

  async function comprar() {
    if (!hasSession()) { router.push(volverA(`/tienda/${id}`)); return; }
    if (!p?.priceGs) return;
    setError(""); setBusy(true);
    try {
      await api("/store/orders", {
        method: "POST",
        body: JSON.stringify({
          items: [{ productId: p.id, qty }],
          delivery,
          address: delivery === "SHIPPING" ? address.trim() : undefined,
          // Va el subtotal SIN descontar: el API compara este número contra el
          // precio de la mercadería y aplica el cupón por su cuenta. Mandarle el
          // total ya descontado hace que rechace el pedido.
          expectedTotalGs: p.priceGs * qty,
          cuponCodigo: cupon?.code,
        }),
      });
      router.push("/tienda/pedidos");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No pudimos confirmar el pedido. Probá de nuevo.");
      setBusy(false);
    }
  }

  const header = (
    <header className="sticky top-0 z-20 border-b border-white/10 bg-[#14191B]/90 backdrop-blur">
      <div className="max-w-4xl mx-auto px-4 sm:px-5 h-16 flex items-center gap-3">
        <Link href="/tienda" aria-label="Volver a la tienda"
          className="w-11 h-11 flex-none rounded-lg border border-white/12 grid place-items-center text-[#B5C0BB] hover:bg-white/5">
          <Icon name="chevron-left" size={20} />
        </Link>
        <span className="font-semibold">Tienda</span>
        <div className="flex-1" />
        <Link href="/tienda/pedidos"
          className="inline-flex min-h-[44px] items-center gap-2 px-2 text-sm font-semibold text-[#B5C0BB] hover:text-[#EDF1EF]">
          <Icon name="package" size={18} /> <span className="hidden sm:inline">Mis pedidos</span>
        </Link>
      </div>
    </header>
  );

  // El header se mantiene mientras carga: sin esto la pantalla entera se
  // reemplazaba por un "Cargando…" y al resolverse saltaba todo el layout.
  if (!p) {
    return (
      <div className="min-h-screen bg-[#14191B] text-[#EDF1EF]">
        {header}
        <main className="max-w-4xl mx-auto px-4 sm:px-5 py-8 grid md:grid-cols-2 gap-8" aria-busy={!errorCarga}>
          {errorCarga ? (
            <div className="md:col-span-2 text-center py-16">
              <Icon name="x-circle" size={36} className="text-[#DE705C] mx-auto" />
              <p className="mt-3 text-[14px] text-[#B5C0BB]">{errorCarga}</p>
              <div className="mt-5 flex flex-col sm:flex-row gap-2 justify-center">
                <button onClick={cargar}
                  className="min-h-[44px] px-6 rounded-xl bg-[#E8A23C] text-[#201404] font-bold hover:brightness-105">
                  Reintentar
                </button>
                <Link href="/tienda"
                  className="min-h-[44px] px-6 rounded-xl border border-white/15 font-semibold inline-flex items-center justify-center hover:bg-white/5">
                  Volver a la tienda
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="aspect-square rounded-2xl border border-white/10 bg-white/4 animate-pulse" />
              <div className="space-y-3">
                <div className="h-3 w-24 bg-white/6 rounded animate-pulse" />
                <div className="h-8 bg-white/6 rounded animate-pulse" />
                <div className="h-8 w-2/3 bg-white/6 rounded animate-pulse" />
                <div className="h-10 w-40 bg-white/6 rounded animate-pulse mt-6" />
              </div>
            </>
          )}
        </main>
      </div>
    );
  }

  const descuento = descuentoDe(p);

  return (
    <div className="min-h-screen bg-[#14191B] text-[#EDF1EF] pb-24">
      {header}

      <main className="max-w-4xl mx-auto px-4 sm:px-5 py-8 grid md:grid-cols-2 gap-8">
        <div className="relative rounded-2xl border border-white/10 overflow-hidden">
          <ProductImage productId={p.id} name={p.name} hasImage={p.hasImage} version={p.imageKey}
            className="w-full aspect-square" iconSize={80} />
          {descuento >= 5 && (
            <span className="absolute top-3 left-3 rounded-full bg-[#E8A23C] text-[#201404] text-[13px] font-bold px-3 py-1.5">
              −{descuento}% mayorista
            </span>
          )}
        </div>

        <div className="flex flex-col">
          <span className="font-mono text-2xs uppercase tracking-[0.14em] text-[#808D88]">{p.sku}</span>
          <h1 className="mt-2 text-2xl font-bold tracking-tight leading-tight text-balance">{p.name}</h1>
          {p.description && <p className="mt-3 text-base leading-relaxed text-[#B5C0BB]">{p.description}</p>}

          <div className="mt-6">
            {descuento >= 5 && (
              <div className="text-[#808D88] line-through tabular-nums">{gs(p.publicPriceGs!)}</div>
            )}
            {/* 48px de precio no entran en 360 px cuando el monto tiene siete
                dígitos: se achica en teléfono y crece desde sm */}
            <div className={`text-[32px] sm:text-4xl font-bold tabular-nums leading-none ${descuento >= 5 ? "text-[#E8A23C]" : ""}`}>
              {p.priceGs != null ? gs(p.priceGs) : "Consultar"}
            </div>
            {p.priceListName && (
              <div className="mt-2 inline-flex items-center gap-1.5 text-[13px] text-[#808D88]">
                <Icon name="tag" size={14} /> {p.priceListName}
              </div>
            )}
          </div>

          <div className="mt-4">
            {p.stock === 0 ? (
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#B5C0BB] bg-white/6 rounded-full px-3 py-1.5">
                <Icon name="x-circle" size={16} /> Sin stock
              </span>
            ) : p.lowStock ? (
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#E8A23C] bg-[#E8A23C]/12 rounded-full px-3 py-1.5">
                <Icon name="alert" size={16} /> Últimas {p.stock} unidades
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#5CBE8F] bg-[#5CBE8F]/12 rounded-full px-3 py-1.5">
                <Icon name="check-circle" size={16} /> En stock ({p.stock} disponibles)
              </span>
            )}
          </div>

          {p.stock === 0 && (
            /* Sin stock no hay nada que tocar: se ofrece la salida en vez de
               dejar la pantalla muerta */
            <Link href="/tienda"
              className="mt-6 inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl border border-white/15 font-semibold hover:bg-white/5">
              <Icon name="search" size={18} /> Ver repuestos disponibles
            </Link>
          )}

          {p.stock > 0 && p.priceGs != null && (
            <div className="mt-6 rounded-2xl border border-white/12 bg-[#1A2123] p-4 flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-[#B5C0BB]">Cantidad</span>
                <div className="flex items-center gap-2 ml-auto">
                  <button onClick={() => setQty((v) => Math.max(1, v - 1))} aria-label="Quitar uno" disabled={qty <= 1}
                    className="w-11 h-11 rounded-lg bg-white/8 grid place-items-center hover:bg-white/12 disabled:opacity-40">
                    <Icon name="minus" size={16} />
                  </button>
                  <span className="w-10 text-center font-bold tabular-nums">{qty}</span>
                  <button onClick={() => setQty((v) => Math.min(p.stock, v + 1))} aria-label="Agregar uno" disabled={qty >= p.stock}
                    className="w-11 h-11 rounded-lg bg-white/8 grid place-items-center hover:bg-white/12 disabled:opacity-40">
                    <Icon name="plus" size={16} />
                  </button>
                </div>
              </div>
              {/* ===== Cupón =====
                  Plegado por defecto: el que no tiene ninguno ve una sola línea
                  de texto y sigue de largo. */}
              {cupon ? (
                <div className="flex items-center gap-2 rounded-lg border border-[#5CBE8F]/30 bg-[#5CBE8F]/10 px-3 py-1.5">
                  <Icon name="tag" size={16} className="text-[#5CBE8F] flex-none" />
                  <span className="text-[13px] flex-1 min-w-0 truncate">
                    Cupón <strong className="font-mono">{cupon.code}</strong>
                    {descuentoGs > 0
                      ? <> · <span className="text-[#5CBE8F] font-semibold">− {gs(descuentoGs)}</span></>
                      /* Cambió la cantidad y todavía no volvió la cuenta nueva:
                         en vez de un número viejo, lo único cierto es que lo
                         aplica el servidor al confirmar. */
                      : " · se aplica al confirmar"}
                  </span>
                  <button onClick={() => { setCupon(null); setCuponTexto(""); setCuponMotivo(""); setCuponAbierto(true); }}
                    className="min-h-[44px] px-2 flex-none text-[13px] font-semibold text-[#808D88] hover:text-[#DE705C]">
                    Quitar
                  </button>
                </div>
              ) : !cuponAbierto ? (
                <button onClick={() => setCuponAbierto(true)}
                  className="self-start inline-flex min-h-[44px] items-center gap-2 text-[13.5px] font-semibold text-[#B5C0BB] hover:text-[#E8A23C]">
                  <Icon name="tag" size={16} /> ¿Tenés un cupón?
                </button>
              ) : (
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <input value={cuponTexto}
                      onChange={(e) => { setCuponTexto(e.target.value); setCuponMotivo(""); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); aplicarCupon(cuponTexto, subtotal); } }}
                      maxLength={40} autoComplete="off" autoCapitalize="characters" spellCheck={false}
                      aria-label="Código del cupón" placeholder="BUSCO-XXXXXX"
                      className="flex-1 min-w-0 rounded-lg bg-[#101516] border border-white/15 px-3 py-3 text-sm font-mono uppercase placeholder-[#808D88] placeholder:font-sans focus:outline-none focus:border-[#E8A23C]" />
                    <button onClick={() => aplicarCupon(cuponTexto, subtotal)} disabled={validando || !cuponTexto.trim()}
                      className="min-h-[44px] px-4 flex-none rounded-lg border border-white/15 text-sm font-bold hover:bg-white/5 disabled:opacity-50">
                      {validando ? "Validando…" : "Aplicar"}
                    </button>
                  </div>
                  {cuponMotivo && (
                    <p className="text-[13px] text-[#DE705C] flex items-start gap-1.5">
                      <Icon name="alert" size={15} className="flex-none mt-0.5" /> {cuponMotivo}
                    </p>
                  )}
                  <div className="flex items-center justify-between gap-2">
                    <Link href="/beneficios"
                      className="inline-flex min-h-[44px] items-center gap-1.5 text-[13px] font-semibold text-[#72A9D8] hover:underline">
                      <Icon name="sparkles" size={15} /> Ver mis cupones
                    </Link>
                    <button onClick={() => { setCuponAbierto(false); setCuponMotivo(""); }}
                      className="min-h-[44px] px-2 text-[13px] text-[#808D88] hover:text-[#EDF1EF]">
                      Cerrar
                    </button>
                  </div>
                </div>
              )}

              {/* Desglose: nadie tiene que sorprenderse con el número final */}
              <div className="border-t border-white/8 pt-3 flex flex-col gap-1.5">
                {descuentoGs > 0 && (
                  <>
                    <div className="flex items-baseline justify-between gap-3 text-[13.5px]">
                      <span className="text-[#B5C0BB]">Subtotal</span>
                      <span className="tabular-nums">{gs(subtotal)}</span>
                    </div>
                    <div className="flex items-baseline justify-between gap-3 text-[13.5px] text-[#5CBE8F]">
                      <span className="truncate">Descuento del cupón</span>
                      <span className="tabular-nums flex-none">− {gs(descuentoGs)}</span>
                    </div>
                  </>
                )}
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm text-[#B5C0BB]">Total</span>
                  <span className="text-lg font-bold tabular-nums">{gs(totalFinal)}</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {([["PICKUP", "store", "Retiro"], ["SHIPPING", "truck", "Envío"]] as const).map(([mode, icon, label]) => (
                  <button key={mode} onClick={() => setDelivery(mode)} aria-pressed={delivery === mode}
                    className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg border text-sm font-semibold transition ${
                      delivery === mode ? "border-[#E8A23C] bg-[#E8A23C]/10 text-[#E8A23C]" : "border-white/15 hover:bg-white/5"
                    }`}>
                    <Icon name={icon} size={16} /> {label}
                  </button>
                ))}
              </div>
              {delivery === "SHIPPING" && (
                <input value={address} onChange={(e) => setAddress(e.target.value)} maxLength={300}
                  aria-label="Dirección de envío" autoComplete="street-address"
                  placeholder="Dirección de envío (calle, número y una referencia)"
                  className="rounded-lg bg-[#101516] border border-white/15 px-4 py-3 text-sm placeholder-[#808D88] focus:outline-none focus:border-[#E8A23C]" />
              )}
              {error && <p className="text-[13.5px] text-[#DE705C] bg-[#DE705C]/10 rounded-lg px-3 py-2.5">{error}</p>}
              <button disabled={busy || (delivery === "SHIPPING" && !address.trim())} onClick={comprar}
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl bg-[#E8A23C] text-[#201404] font-bold py-3.5 hover:bg-[#F2B054] active:bg-[#D8912C] transition disabled:opacity-50">
                {busy ? "Confirmando…" : <><Icon name="cart" size={18} /> {sesion ? "Confirmar pedido" : "Ingresar y confirmar"}</>}
              </button>
              <p className="text-[13px] text-[#808D88] text-center">
                {sesion ? "Pagás al retirar o recibir. Sin cargo por reservar." : "Ingresás y volvés a este repuesto para confirmar."}
              </p>
            </div>
          )}

          <div className="mt-6 space-y-3">
            {[
              { i: "shield-check" as const, t: "Garantía de 30 días", d: "Cubre defectos de fábrica del repuesto. Traés el comprobante y lo cambiamos." },
              { i: "package-check" as const, t: "Repuesto verificado", d: "Trabajamos con service packs y componentes certificados, no con genéricos sin marca." },
              { i: "handshake" as const, t: "Asesoría técnica", d: "¿No sabés si es el repuesto que necesitás? Preguntanos antes de comprar." },
            ].map((c) => (
              <div key={c.t} className="flex gap-3">
                <span className="w-9 h-9 rounded-lg bg-[#5CBE8F]/12 text-[#5CBE8F] grid place-items-center flex-none">
                  <Icon name={c.i} size={18} />
                </span>
                <div>
                  <div className="font-semibold text-sm">{c.t}</div>
                  <div className="text-[13px] leading-relaxed text-[#B5C0BB]">{c.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
